/**
 * Guide data for the public guide pages: the API (database, edited in
 * Admin > Guides / Itinerary Guides) first, the shipped JSON files
 * (/topical-guides.json, /regional-guides.json) as the fallback so the
 * pages never go blank.
 *
 * Fallback happens on a network error, a timeout (5s), a 5xx or any other
 * unexpected answer — NOT on the API's own "Guide not found" 404, because
 * that is how an unpublished or deleted guide disappears. A bare 404 from a
 * backend that doesn't have the endpoint yet (detail "Not Found") still
 * falls back.
 *
 * Every loader takes an optional `onSource(source, reason)` callback:
 * source is 'api' or 'fallback' (pages put it on data-guide-source). A
 * fallback also logs one console.warn per API path with the reason
 * (timeout / network / status NNN / unexpected body), so a page quietly
 * showing the shipped JSON is visible in the browser console.
 */
import { getBackendUrl } from './runtimeConfig';

export const GUIDES_API_TIMEOUT_MS = 5000;
export const GUIDE_NOT_FOUND = 'Guide not found';

const apiUrl = (path) => `${getBackendUrl()}/api${path}`;

const warnedPaths = new Set();

/** Test helper: forget which fallbacks were already warned about. */
export function resetFallbackWarnings() {
  warnedPaths.clear();
}

function errorReason(err) {
  return err && err.message === 'timeout' ? 'timeout' : 'network';
}

function noteFallback(apiPath, reason, onSource) {
  if (!warnedPaths.has(apiPath)) {
    warnedPaths.add(apiPath);
    console.warn(`[guides] /api${apiPath} unavailable (${reason}); showing the bundled JSON fallback`);
  }
  if (onSource) onSource('fallback', reason);
}

/** fetch + JSON with a timeout. Resolves { status, data }; rejects on a
 *  network error or timeout. */
export async function fetchJson(url, { timeoutMs = GUIDES_API_TIMEOUT_MS, fetchImpl } = {}) {
  const doFetch = fetchImpl || ((...a) => fetch(...a));
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (controller) controller.abort();
      reject(new Error('timeout'));
    }, timeoutMs);
  });
  try {
    const res = await Promise.race([
      doFetch(url, controller ? { signal: controller.signal } : undefined),
      timeout,
    ]);
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    return { status: res.status, data };
  } finally {
    clearTimeout(timer);
  }
}

async function fromFallback(fallbackUrl, pick, fetchImpl) {
  try {
    const { data } = await fetchJson(fallbackUrl, { fetchImpl });
    return pick(data || {});
  } catch {
    return pick({});
  }
}

/**
 * One guide. Returns the guide object, or null when it doesn't exist /
 * isn't published.
 *   apiPath      e.g. "/guides/by-slug/napa/castello-di-amorosa"
 *   fallbackUrl  e.g. "/topical-guides.json"
 *   match        (guide) => boolean, picks the guide out of the JSON file
 */
export async function loadGuide({ apiPath, fallbackUrl, match, timeoutMs, fetchImpl, onSource }) {
  const pick = (json) => (json.guides || []).find(match) || null;
  let reason;
  try {
    const { status, data } = await fetchJson(apiUrl(apiPath), { timeoutMs, fetchImpl });
    if (status === 200 && data && typeof data === 'object' && !Array.isArray(data)) {
      if (onSource) onSource('api');
      return data;
    }
    if (status === 404 && data?.detail === GUIDE_NOT_FOUND) {
      if (onSource) onSource('api');
      return null;
    }
    reason = status === 200 ? 'unexpected body' : `status ${status}`;
  } catch (err) { reason = errorReason(err); }
  noteFallback(apiPath, reason, onSource);
  return fromFallback(fallbackUrl, pick, fetchImpl);
}

/**
 * A list of published guides. `fromApi(data)` turns the API body into the
 * array (or returns null if the body looks wrong, which falls back).
 */
export async function loadGuideList({ apiPath, fallbackUrl, fromApi, timeoutMs, fetchImpl, onSource }) {
  let reason;
  try {
    const { status, data } = await fetchJson(apiUrl(apiPath), { timeoutMs, fetchImpl });
    const list = status === 200 ? fromApi(data) : null;
    if (Array.isArray(list)) {
      if (onSource) onSource('api');
      return list;
    }
    reason = status === 200 ? 'unexpected body' : `status ${status}`;
  } catch (err) { reason = errorReason(err); }
  noteFallback(apiPath, reason, onSource);
  return fromFallback(fallbackUrl, (json) => json.guides || [], fetchImpl);
}

export const loadTopicalGuide = (region, slug, opts = {}) => loadGuide({
  apiPath: `/guides/by-slug/${encodeURIComponent(region)}/${encodeURIComponent(slug)}`,
  fallbackUrl: '/topical-guides.json',
  match: (g) => g.slug === `${region}/${slug}`,
  ...opts,
});

export const loadRegionalGuide = (region, intentSlug, opts = {}) => loadGuide({
  apiPath: `/regional-guides/${encodeURIComponent(region)}/${encodeURIComponent(intentSlug)}`,
  fallbackUrl: '/regional-guides.json',
  match: (g) => g.regionSlug === region && g.intentSlug === intentSlug,
  ...opts,
});

export const loadRegionalGuides = (opts = {}) => loadGuideList({
  apiPath: '/regional-guides',
  fallbackUrl: '/regional-guides.json',
  fromApi: (data) => (Array.isArray(data?.guides) ? data.guides : null),
  ...opts,
});

export const loadTopicalGuides = (opts = {}) => loadGuideList({
  apiPath: '/guides',
  fallbackUrl: '/topical-guides.json',
  fromApi: (data) => (Array.isArray(data) ? data : null),
  ...opts,
});
